import { useEffect } from "react";
import simbApi from "../../utils/requests";
import { analytics } from "../../utils/segment/analyticsService";
import useUser from "../context/userHook";
import useBoardAndItems from "../context/useBoardAndItems";
import logger from "../../lib/logger";
import useMondayContext from "@hooks/context/useMondayContext";

/**
 * Activation nudge trigger — fires when the app loads in an item view.
 *
 * Does two things:
 * 1. Tracks share_flow_opened (the activation-nudges spec section 6
 *    "missing event") with groupId for org-level analytics.
 * 2. Notifies the backend, which handles nudge attribution (was this visit
 *    caused by a nudge notification?) and the A1 trigger gates (suppression,
 *    dedupe, scheduling).
 *
 * sessionStorage guard prevents re-firing on re-renders within the same
 * session; the backend's account-level dedupe is the real guarantee.
 * Fire-and-forget: a nudge must never affect the user's session.
 */
export const useNudgeTrigger = () => {
  const { userId, accountId } = useUser();
  const { appFeatureType } = useMondayContext();
  const { currentBoard, itemIds } = useBoardAndItems();

  const itemId = appFeatureType === "AppFeatureItemView" ? itemIds?.[0] : null;

  useEffect(() => {
    if (!userId || !accountId || !itemId) return;

    const guardKey = `nudge-trigger-${itemId}`;
    if (sessionStorage.getItem(guardKey)) return;
    sessionStorage.setItem(guardKey, "1");

    analytics.track("share_flow_opened", {
      groupId: `${accountId}`,
      board_id: `${currentBoard ?? ""}`,
      item_id: `${itemId}`,
      view_type: "item_view",
    });

    simbApi
      .post("/nudges/trigger", {
        userid: `${userId}`,
        accountid: `${accountId}`,
        boardid: `${currentBoard ?? ""}`,
        itemid: `${itemId}`,
      })
      .catch((error) => {
        // best-effort — log and move on, never disturb the user
        logger.warn?.("nudge trigger failed", error);
      });
  }, [userId, accountId, currentBoard, itemId]);
};

export default useNudgeTrigger;
